import React, { useCallback, useContext, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Alert, Box, Button, CircularProgress, GridLegacy as Grid, Stack, Typography } from '@mui/material';

import { AccommodationPropertyDetailsContext } from '~/components/Accommodation/Pages/PropertyDetails/context/AccommodationPropertyDetailsContext';
import useAccommodationPropertyDetailsContext from '~/components/Accommodation/Pages/PropertyDetails/context/useAccommodationPropertyDetailsContext';
import DefinitionText from '~/components/Common/Elements/DefinitionText';
import DefinitionTitle from '~/components/Common/Elements/DefinitionTitle';
import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { ROLE_ADMIN_USER, ROLE_COORDINATOR, ROLE_EMPLOYEE_USER, ROLE_HOTEL_COORDINATOR } from '~/consts/roles';

import {
  useGetPropertyContentSources,
  useUpdatePropertyContentSources,
} from '~/hooks/apis/accommodation/usePropertyContentSources';
import useToggleState from '~/hooks/useToggleState';

import {
  UpdatePropertyContentSourcesPayload,
  getPropertyContent,
  initiateOfferContentGeneration,
} from '~/services/AccommodationService';

import GenerateContentModal, { InitiateContentGenerationRequest } from '../../../../../Common/GenerateContentModal';
import ContentSourceFieldRow, {
  getActiveSource,
  getSelectableSources,
} from '../AccommodationPropertyContentSources/ContentSourceFieldRow';
import useContentSourceFieldStates from '../AccommodationPropertyContentSources/useContentSourceFieldStates';

import AccommodationGeneratedContentView from './AccommodationGeneratedContentView';

const CONTENT_ROLES = [ROLE_ADMIN_USER, ROLE_COORDINATOR, ROLE_HOTEL_COORDINATOR];
const EDIT_ROLES = [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_HOTEL_COORDINATOR];
const EXCLUDE_CUSTOM_FIELDS = ['address'];
const CUSTOM_CONTENT_FIELD_ORDER = [
  'tagline',
  'description',
  'additionalDescription',
  'locationDescription',
  'facilities',
  'highlights',
  'finePrint',
];
const CUSTOM_CONTENT_FIELD_ORDER_BY_FIELD = new Map(CUSTOM_CONTENT_FIELD_ORDER.map((field, index) => [field, index]));

export default function AccommodationPropertyDetailsSection() {
  const { enqueueSnackbar } = useSnackbar();
  const { propertyDetails, propertyContent } = useAccommodationPropertyDetailsContext();
  const { setPropertyContent } = useContext(AccommodationPropertyDetailsContext);
  const [useWebSearch, setUseWebSearch] = useState(false);
  const [contentTypeToGenerate, setContentTypeToGenerate] = useState<InitiateContentGenerationRequest['contentType']>();

  const {
    data: contentSourcesData,
    isLoading: isContentSourcesLoading,
    error: contentSourcesError,
  } = useGetPropertyContentSources({ propertyId: propertyDetails.id });
  const updateMutation = useUpdatePropertyContentSources();
  const {
    getChangedFields,
    getFieldState,
    handleFieldChange,
    hasChanges: hasContentSourceChanges,
  } = useContentSourceFieldStates(contentSourcesData?.propertyFields, contentSourcesData?.availableSources);

  const handleSaveContentSources = useCallback(() => {
    if (!contentSourcesData) {
      return;
    }

    const changedFields = getChangedFields() as UpdatePropertyContentSourcesPayload['fields'];
    if (changedFields.length === 0) {
      return;
    }

    updateMutation.mutate(
      { propertyId: propertyDetails.id, data: { fields: changedFields } },
      {
        onSuccess: () => {
          enqueueSnackbar('Property content sources updated', { variant: 'success' });
        },
        onError: (err: Error) => {
          enqueueSnackbar(`Failed to update: ${err.message}`, { variant: 'error' });
        },
      },
    );
  }, [contentSourcesData, getChangedFields, propertyDetails.id, updateMutation, enqueueSnackbar]);

  const {
    isToggled: isGenerateContentModalOpen,
    toggleOn: openGenerateContentModal,
    toggleOff: closeGenerateContentModal,
  } = useToggleState(false);

  const hasGeneratedContent = () => propertyContent?.generatedContents.length > 0;
  const connectedOffer = useMemo(
    () => propertyDetails.connectedOffers.find((offer) => offer.idSalesforceExternal && offer.offerType !== 'Bedbank'),
    [propertyDetails.connectedOffers],
  );
  const canGenerateContent = Boolean(connectedOffer?.idSalesforceExternal);

  const fetchContent = async () => {
    try {
      const response = await getPropertyContent({ propertyId: propertyDetails.id });
      if (response.result && setPropertyContent) {
        setPropertyContent(response.result);
      } else {
        enqueueSnackbar(`Failed to fetch property content: ${JSON.stringify(response)}`, {
          variant: 'error',
        });
      }
    } catch (error) {
      enqueueSnackbar(`Failed to fetch property content: ${error.message}`, { variant: 'error' });
    }
  };

  const showGenerateContentModal = (contentType: InitiateContentGenerationRequest['contentType']) => {
    setContentTypeToGenerate(contentType);
    openGenerateContentModal();
  };

  const initiateContentGeneration = (params: InitiateContentGenerationRequest) => {
    return initiateOfferContentGeneration({
      offerId: connectedOffer?.idSalesforceExternal ?? '',
      ...params,
    });
  };

  return (
    <>
      <Grid container spacing={4}>
        <Grid item xs={3}>
          <Stack direction="column" gap={4}>
            <Box>
              <DefinitionTitle>Country</DefinitionTitle>
              <DefinitionText>{propertyDetails.country || 'Unknown'}</DefinitionText>
            </Box>
            <Box>
              <DefinitionTitle>City</DefinitionTitle>
              <DefinitionText>{propertyDetails.city || 'Unknown'}</DefinitionText>
            </Box>
            <Box>
              <DefinitionTitle>Rating</DefinitionTitle>
              <DefinitionText>{propertyDetails.rating}</DefinitionText>
            </Box>
          </Stack>
        </Grid>
        <Grid item xs={9}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" align="center">
              {hasGeneratedContent() ? 'Generated Content' : 'No generated content'}
            </Typography>
            <Stack direction="row" spacing={1}>
              <PermissionedComponent requiredRoles={CONTENT_ROLES}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => showGenerateContentModal('description')}
                  disabled={!canGenerateContent}
                >
                  {hasGeneratedContent() ? 'Regenerate Description' : 'Generate Description'}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => showGenerateContentModal('nearby')}
                  disabled={!canGenerateContent}
                >
                  {hasGeneratedContent() ? 'Regenerate Nearby' : 'Generate Nearby'}
                </Button>
              </PermissionedComponent>
            </Stack>
          </Stack>
          {hasGeneratedContent() && (
            <PermissionedComponent requiredRoles={CONTENT_ROLES}>
              <AccommodationGeneratedContentView content={propertyContent.generatedContents} />
            </PermissionedComponent>
          )}
        </Grid>
      </Grid>

      <Stack direction="column" spacing={4}>
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Set custom content for A/B experiment variants.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select the source override.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Fields left empty have the best source selected when serving the content.
              </Typography>
            </Box>
            <PermissionedComponent requiredRoles={EDIT_ROLES}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveContentSources}
                disabled={updateMutation.isPending || !hasContentSourceChanges}
              >
                Save All
              </Button>
            </PermissionedComponent>
          </Stack>

          {isContentSourcesLoading && (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} />
            </Box>
          )}

          {contentSourcesError && (
            <Alert severity="error">
              Error loading content sources:{' '}
              {contentSourcesError instanceof Error ? contentSourcesError.message : 'Unknown error'}
            </Alert>
          )}

          {contentSourcesData && (
            <PermissionedComponent requiredRoles={EDIT_ROLES}>
              <Stack spacing={1}>
                {contentSourcesData.propertyFields
                  .filter((fieldSelection) => !EXCLUDE_CUSTOM_FIELDS.includes(fieldSelection.field))
                  .slice()
                  .sort((a, b) => {
                    const aOrder = CUSTOM_CONTENT_FIELD_ORDER_BY_FIELD.get(a.field) ?? Number.MAX_SAFE_INTEGER;
                    const bOrder = CUSTOM_CONTENT_FIELD_ORDER_BY_FIELD.get(b.field) ?? Number.MAX_SAFE_INTEGER;

                    return aOrder - bOrder;
                  })
                  .map((fieldSelection) => {
                    const activeSource = getActiveSource(fieldSelection, contentSourcesData.availableSources);

                    return (
                      <ContentSourceFieldRow
                        key={fieldSelection.field}
                        fieldSelection={fieldSelection}
                        activeSource={activeSource}
                        availableSources={getSelectableSources(fieldSelection, contentSourcesData.availableSources)}
                        fieldState={getFieldState(fieldSelection)}
                        onChange={handleFieldChange}
                        isPending={updateMutation.isPending}
                        allowEditSourceAsCustom
                      />
                    );
                  })}
              </Stack>
            </PermissionedComponent>
          )}
        </Box>
      </Stack>

      {isGenerateContentModalOpen && (
        <GenerateContentModal
          onClose={closeGenerateContentModal}
          initiateContentGeneration={initiateContentGeneration}
          fetchContent={fetchContent}
          includeOfferInGeneration={hasGeneratedContent()}
          setUseWebSearch={setUseWebSearch}
          useWebSearch={useWebSearch}
          contentType={contentTypeToGenerate}
        />
      )}
    </>
  );
}
